import { Card, Button, Collapse, Select, Row, Col, Checkbox, Tooltip, Spin, Typography } from 'antd';
import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import releaseDetialsApiList from '../../api/ReleaseDetailsApi';
import useApi from '../../hooks/useApi';
import ValueDisplay from './ValueDisplay';
import FullDetailsModal from './FullDetailsModal';

import { IReleaseItems } from '../../interfaces/IReleaseItems';
import ReworkBarChart from './ReworkBarChart';


import { useLocation, useNavigate } from 'react-router-dom';
import { MyContext } from '../../context/myContext';
import ProductDropdown from '../Performance Dashboard/productsDropdown';
import { getProductList } from '../../utils/DataService';
import dayjs from 'dayjs';
import { DownloadOutlined } from '@ant-design/icons';

const { Panel } = Collapse;
const { Option } = Select;
const { Text } = Typography;

const compareVersions = (v1: string, v2: string): number => {
    const parseVersion = (version: string) =>
        version.split(/[-.]/).map((part) => (isNaN(Number(part)) ? part : Number(part)));

    const v1Parts = parseVersion(v1);
    const v2Parts = parseVersion(v2);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
        const part1 = v1Parts[i] ?? 0;
        const part2 = v2Parts[i] ?? 0;

        if (part1 < part2) return -1;
        if (part1 > part2) return 1;
    }
    return 0;
};

export const parseDuration = (duration: string) => {
    let days = 0, hours = 0, minutes = 0, seconds = 0;

    if (duration) {
        const dayMatch = duration.match(/(\d+(\.\d+)?)\s*day(s?)/);
        const timeMatch = duration.match(/(\d+):(\d+):(\d+)/);

        if (dayMatch) days = parseFloat(dayMatch[1]) || 0;

        if (timeMatch) {
            hours = parseInt(timeMatch[1], 10) || 0;
            minutes = parseInt(timeMatch[2], 10) || 0;
            seconds = parseInt(timeMatch[3], 10) || 0;
        }
    }

    const totalDays = days + (hours / 24) + (minutes / 1440) + (seconds / 86400);

    return { days, hours, minutes, seconds, totalDays };
};

const getFilterStorageKey = (product: string) => `releaseComparisonFilters_${product}`;
enum ExecutionTime {
    TOTAL = "total",
    FIRST_PASS = "firstPass",
    REWORK = "rework",
}

const getLeadTimeUnits = (releaseDetails: string | any[]) => {
    if (!releaseDetails || releaseDetails.length === 0) return "";
    return releaseDetails[0]?.state_categories?.[0]?.datasets?.[0]?.units || "";
};

export const ALL = 'All'

const calculateDropdownWidth = (selectedReleases: string[]) => {
    if (selectedReleases.length === 0) return "150px";
    const totalWidth = selectedReleases.reduce((acc, release) => acc + release.length * 20, 0);
    return `${Math.min(Math.max(totalWidth, 150), 430)}px`;
};


export const getStateCategories = (releaseDetails: any[]): string[] => {
    const categorySet = new Set<string>();

    releaseDetails.forEach(item => {
        item.releases?.forEach((release: any) => {
            release.work_items?.forEach((workItem: any) => {
                workItem.execution_time?.forEach((execution: any) => {
                    if (execution?.category) {
                        categorySet.add(execution.category);
                    }
                });
            });
        });
    });

    return Array.from(categorySet);
};


const ReleaseComparison: React.FC = () => {
    const getReleaseDetailsApi = useApi(releaseDetialsApiList.getReleaseDetails)
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedReleaseItem, setSelectedReleaseItem] = useState<any>(null);
    const [selectedReleases, setSelectedReleases] = useState<string[]>([]);
    const [selectedType, setSelectedType] = useState<string>(ALL);
    const [comparisonMode, setComparisonMode] = useState<string>("target");
    const [filteredData, setFilteredData] = useState<IReleaseItems[]>([]);
    const [isFirstPassChecked, setIsFirstPassChecked] = useState(true);
    const [isReworkChecked, setIsReworkChecked] = useState(true);
    const [insightDescription, setInsightDescription] = useState<string>("");
    const { selectedInsightWorkItems, setWorkItemClicked, selectedProduct, setIsJourneyView, stateCategories, setStateCategories, selectedStateCategory, setSelectedStateCategory, releaseDetails, setReleaseDetials } = useContext<any>(MyContext);
    const [insightWorkItems, setInsightWorkItems] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [executionTime, setExecutionTime] = useState<string>(ExecutionTime.TOTAL)
    const location = useLocation();
    const navigate = useNavigate();
    const productList = JSON.parse(getProductList()) || [];
    const hasFetchedFilters = useRef(false);
    const [workItemTypes, setWorkItemTypes] = useState<string[]>([]);
    const [filters, setFilters] = useState(() => {
        const storedFilters = localStorage.getItem(getFilterStorageKey(selectedProduct));
        return storedFilters
            ? JSON.parse(storedFilters)
            : {
                compareWith: comparisonMode,
                releases: selectedReleases,
                workItemType: selectedType,
                firstPass: isFirstPassChecked,
                rework: isReworkChecked,
                executionTime: executionTime
            };
    });

    useEffect(() => {
        hasFetchedFilters.current = false;
        
        const storedFilters = localStorage.getItem(getFilterStorageKey(selectedProduct));
        if (storedFilters) {
            setFilters(JSON.parse(storedFilters));
        } else {
            setFilters({
                compareWith: "target",
                releases: [],
                workItemType: ALL,
                firstPass: true,
                rework: true,
                executionTime: ExecutionTime.TOTAL
            });
        }
    }, [selectedProduct]);

    const updateFilter = (key: string, value: any) => {
        const updatedFilters = { ...filters, [key]: value };
        setFilters(updatedFilters);
    };

    const getFiltersFromStorage = () => {
        const storedFilters = JSON.parse(localStorage.getItem(getFilterStorageKey(selectedProduct)) as string);
        if (storedFilters) {
            if(storedFilters?.releases?.length) { 
                const releases = releaseDetails.map((i: { releases: any; }) => i.releases).map((rel: any[]) => rel.map((item)=>item.release))[0]
                const allExist = storedFilters?.releases.every((item:string) => releases.includes(item));
                if(!allExist && releaseDetails.length){
                    setSelectedReleases([])
                    storedFilters.releases = []
                }else{
                    setSelectedReleases(storedFilters?.releases)
                }
            }

            storedFilters?.compareWith && setComparisonMode(storedFilters?.compareWith)
            setIsFirstPassChecked(storedFilters?.firstPass)
            storedFilters?.workItemType && setSelectedType(storedFilters?.workItemType)
            setIsReworkChecked(storedFilters?.rework)
            storedFilters?.executionTime && setExecutionTime(storedFilters?.executionTime)
            storedFilters?.releases.length && handleFilterChange(storedFilters?.releases , storedFilters?.workItemType, storedFilters?.compareWith, selectedStateCategory)
            localStorage.setItem(getFilterStorageKey(selectedProduct), JSON.stringify(storedFilters));
        }
    }

    useEffect(() => {
        if (Array.isArray(releaseDetails) && releaseDetails.length > 0 && !hasFetchedFilters.current) {
            getFiltersFromStorage();
            hasFetchedFilters.current = true;
        }
        const hasEmptyStateCategories =
        Array.isArray(filteredData) &&
        filteredData.length > 0 &&
        Array.isArray(filteredData[0]?.state_categories) &&
        filteredData[0].state_categories.length === 0;

    if (comparisonMode === "target" && hasEmptyStateCategories) {
        setComparisonMode("previousRelease");
    }
    }, [ releaseDetails, selectedProduct]);

    const leadTimeUnits = getLeadTimeUnits(releaseDetails);

    const executionTimeOptions = [
        { label: "Total", value: ExecutionTime.TOTAL },
        { label: "First Pass", value: ExecutionTime.FIRST_PASS },
        { label: "Rework", value: ExecutionTime.REWORK },
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getReleaseDetailsApi.request(selectedProduct);
                setReleaseDetials(response?.data?.release_details);
                setLoading(false)
                hasFetchedFilters.current = false;
            } catch (error) {
                console.error("Error fetching metrics:", error);
            }
        };

        fetchData();
    }, [selectedProduct]);


    useEffect(() => {
        localStorage.setItem(getFilterStorageKey(selectedProduct), JSON.stringify(filters));
    }, [filters, selectedProduct]);

    const showModal = (releaseItem: any) => {
        setSelectedReleaseItem(releaseItem);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setSelectedReleaseItem(null);
    };

    const getUniqueTypes = (releaseDetails: any[]) => {
        const types = new Set<string>();
        if (!releaseDetails.length) return [];

        releaseDetails?.forEach((releaseDetail) => {
            releaseDetail?.releases?.forEach((release: any) => {
                release?.work_items?.forEach((workItem: any) => {
                    if (workItem.type) {
                        types.add(workItem.type);
                    }
                });
            });
        });

        return Array.from(types);
    };

    useEffect(() => {
        if (releaseDetails) {
            const uniqueTypes = getUniqueTypes(releaseDetails);
            setWorkItemTypes(uniqueTypes);
            const stateCategoriesData = getStateCategories(releaseDetails);
            setStateCategories(stateCategoriesData);
        } else {
            setWorkItemTypes([]);
            setStateCategories([]);
        }
    }, [releaseDetails]);


    const processReleaseDetails = (comparisonMode: string, selectedOptions: { firstPass: boolean; rework: boolean }) => {
        const releaseDifferences: { [key: string]: { category: string, difference: number }[] } = {};
        const categoryAverageDifferences: { [key: string]: number } = {};
        let totalOverallDifference = 0;

        filteredData.forEach((item: { releases: any[] }) => {
            item.releases.forEach((releaseItem, index) => {
                const categoryDurations: { [key: string]: number } = {};
                const categoryCounts: { [key: string]: number } = {};

                if (releaseItem.work_items && releaseItem.work_items.length > 0) {
                    releaseItem.work_items.forEach((workItem: { execution_time: any[] }) => {
                        workItem.execution_time.forEach((time) => {
                            const firstPassDuration = selectedOptions.firstPass
                                ? parseDuration(time.first_pass).totalDays
                                : 0;
                            const reworkDuration = selectedOptions.rework
                                ? parseDuration(time.rework).totalDays
                                : 0;

                            const totalDuration = firstPassDuration + reworkDuration;

                            if (categoryDurations[time.category]) {
                                categoryDurations[time.category] += totalDuration;
                                categoryCounts[time.category] += 1;
                            } else {
                                categoryDurations[time.category] = totalDuration;
                                categoryCounts[time.category] = 1;
                            }
                        });
                    });
                }

                let overallDifference = 0;

                Object.keys(categoryDurations).forEach((category) => {
                    const duration = categoryDurations[category];
                    const averageDuration = duration / releaseItem.work_items.length;

                    let difference = 0;

                    if (comparisonMode === "target") {
                        const target =
                        filteredData[0]?.state_categories
                            ?.flatMap((sc: any) => sc.datasets)
                            ?.find((dataset: any) => dataset.category === category)?.target || 0;
                
                    difference = averageDuration - target;
                    } else if (comparisonMode === "previousRelease") {
                        if (index > 0) {
                            const previousRelease = item.releases[index - 1];
                            const previousAverage = calculateAverageDuration(previousRelease, category, selectedOptions);
                            difference = averageDuration - previousAverage;
                        } else {
                            difference = averageDuration;
                        }
                    }

                    if (!releaseDifferences[releaseItem.release]) {
                        releaseDifferences[releaseItem.release] = [];
                    }

                    releaseDifferences[releaseItem.release].push({ category, difference });

                    if (!categoryAverageDifferences[category]) {
                        categoryAverageDifferences[category] = 0;
                    }
                    categoryAverageDifferences[category] += difference;

                    overallDifference += difference;
                });

                if (!releaseDifferences[releaseItem.release]) {
                    releaseDifferences[releaseItem.release] = [];
                }

                releaseDifferences[releaseItem.release].push({
                    category: "Overall",
                    difference: overallDifference,
                });

                totalOverallDifference += overallDifference;
            });
        });

        const releaseCount = filteredData.reduce(
            (sum: number, item: { releases: any[] }) => sum + item.releases.length,
            0
        );

        const averages = Object.keys(categoryAverageDifferences).map((category) => ({
            category,
            difference: categoryAverageDifferences[category] / releaseCount,
        }));

        const totalAverageDifference = totalOverallDifference / releaseCount;

        averages.push({
            category: "Overall",
            difference: totalAverageDifference,
        });

        releaseDifferences["averages"] = averages;

        return { releaseDifferences, totalAverageDifference };
    };

    const calculateAverageDuration = (release: any, category: string, selectedOptions: { firstPass: boolean; rework: boolean }) => {
        const categoryDurations: { [key: string]: number } = {};

        if (release.work_items && release.work_items.length > 0) {
            release.work_items.forEach((workItem: { execution_time: any[] }) => {
                workItem.execution_time.forEach((time) => {
                    if (time.category === category) {
                        const firstPassDuration = selectedOptions.firstPass
                            ? parseDuration(time.first_pass).totalDays
                            : 0;
                        const reworkDuration = selectedOptions.rework
                            ? parseDuration(time.rework).totalDays
                            : 0;

                        const totalDuration = firstPassDuration + reworkDuration;

                        if (categoryDurations[time.category]) {
                            categoryDurations[time.category] += totalDuration;
                        } else {
                            categoryDurations[time.category] = totalDuration;
                        }
                    }
                });
            });
        }

        const duration = categoryDurations[category] || 0;
        const workItemCount = release.work_items.length;
        return duration / workItemCount;
    };


    const selectedOptions = {
        firstPass: isFirstPassChecked,
        rework: isReworkChecked,
    };
    const { releaseDifferences, totalAverageDifference } = processReleaseDetails(comparisonMode, selectedOptions);


    const handleFilterChange = (
        selectedReleases: string[],
        selectedType: string,
        comparisonMode: string,
        selectedStateCategory: string
    ) => {
        if ((!selectedReleases || selectedReleases.length === 0) && (!selectedType)) {
            const maxReleases = comparisonMode === 'previousRelease' ? 4 : 3;
            const sortedReleases = releaseDetails
                ?.flatMap((releaseDetail: any) => releaseDetail.releases)
                .map((release: any) => release.release)
                .sort(compareVersions);
    
            const newSelectedReleases = sortedReleases.slice(-maxReleases);
            setSelectedReleases(newSelectedReleases || []);
    
            const filteredData = releaseDetails.map((releaseDetail: any) => ({
                ...releaseDetail,
                releases: releaseDetail.releases.filter((release: any) =>
                    newSelectedReleases.includes(release.release)
                )
            }));
    
            setFilteredData(filteredData);
            return;
        }
        let filteredMetrics = releaseDetails;
        if (selectedReleases?.length > 0) {
            filteredMetrics = filteredMetrics
                .filter((item: any) =>
                    item.releases.some((release: any) =>
                        selectedReleases.includes(release.release)
                    )
                )
                .map((item: any) => ({
                    ...item,
                    releases: item.releases
                        .filter((release: any) =>
                            selectedReleases.includes(release.release)
                        )
                        .sort((a: any, b: any) =>
                            selectedReleases.indexOf(a.release) - selectedReleases.indexOf(b.release)
                        ),
                }));
        }

        if (selectedType === ALL) {
            const categoryMap: Record<string, { target: any; units: string }> = {};
            const workItemTypes = new Set<string>();
    
            releaseDetails.forEach((item: any) => {
                item.state_categories?.forEach((stateCategory: any) => {
                    if (stateCategory.work_item_type) {
                        workItemTypes.add(stateCategory.work_item_type);
                    }
        
                    stateCategory.datasets.forEach((dataset: any) => {
                        const targetValue = parseFloat(dataset.target);
        
                        if (!isNaN(targetValue)) {
                            if (categoryMap[dataset.category]) {
                                categoryMap[dataset.category].target += targetValue;
                            } else {
                                categoryMap[dataset.category] = {
                                    target: targetValue,
                                    units: dataset.units,
                                };
                            }
                        }else{
                            if (!categoryMap[dataset.category]) {
                                categoryMap[dataset.category] = {
                                    target: null,
                                    units: dataset.units,
                                };
                            }
                        }
                    });
                });
            });
        
            const workItemTypeCount = workItemTypes.size || 1;
    
            const mergedStateCategories = [
                {
                    datasets: Object.entries(categoryMap).map(([category, data]) => ({
                        category,
                        target: data.target !== null ? data.target / workItemTypeCount : null,
                        units: data.units,
                    })),
                },
            ];
    
            filteredMetrics = filteredMetrics.map((item: any) => ({
                ...item,
                state_categories: mergedStateCategories,
            }));
        }
    
        if (selectedType && selectedType !== ALL) {
            filteredMetrics = filteredMetrics.map((item: any) => ({
                ...item,
                releases: item.releases.map((release: any) => ({
                    ...release,
                    work_items: release.work_items.filter((workItem: any) => workItem.type === selectedType),
                })),
                state_categories: item.state_categories?.filter(
                    (stateCategory: any) => stateCategory.work_item_type === selectedType
                ) || [],
            }));
        }
        const stateCategoriesData = getStateCategories(releaseDetails);
        if (selectedStateCategory && stateCategoriesData.includes(selectedStateCategory)) {
            const startIndex = stateCategoriesData.indexOf(selectedStateCategory);
            const allowedCategories = stateCategoriesData.slice(startIndex);
    
            filteredMetrics = filteredMetrics.map((item: any) => {
                const filteredStateCategories = item.state_categories?.[0]?.datasets?.filter(
                    (ds: any) => allowedCategories.includes(ds.category)
                ) || [];
    
                return {
                    ...item,
                    state_categories: [
                        {
                            ...item.state_categories?.[0],
                            datasets: filteredStateCategories,
                        },
                    ],
                    releases: item.releases.map((release: any) => ({
                        ...release,
                        work_items: release.work_items.map((workItem: any) => ({
                            ...workItem,
                            execution_time: workItem.execution_time?.filter(
                                (et: any) => allowedCategories.includes(et.category)
                            ),
                        })),
                    })),
                };
            });
        }
    
        setFilteredData(filteredMetrics);
    };


    const handleReleaseChange = (values: string[]) => {
        const maxReleases = comparisonMode === 'previousRelease' ? 4 : 3;
        let newSelectedReleases = values;
    
        if (newSelectedReleases.length === 0) {
            const sortedReleases = releaseDetails
                ?.flatMap((releaseDetail: any) => releaseDetail.releases)
                .map((release: any) => release.release)
                .sort(compareVersions);
    
            newSelectedReleases = sortedReleases.slice(-maxReleases);
        }
    
        setSelectedReleases(newSelectedReleases);
        handleFilterChange(newSelectedReleases, selectedType, comparisonMode, selectedStateCategory);
        updateFilter("releases", newSelectedReleases);
    };

    const handleCheckboxChange = (release: string) => {
        const maxReleases = comparisonMode === 'previousRelease' ? 4 : 3;
    
        let newSelectedReleases = selectedReleases.includes(release)
            ? selectedReleases.filter((r) => r !== release)
            : [...selectedReleases, release].slice(0, maxReleases);

        if (newSelectedReleases.length === 0) {
            const sortedReleases = releaseDetails
                ?.flatMap((releaseDetail: any) => releaseDetail.releases)
                .map((release: any) => release.release)
                .sort(compareVersions);
    
            newSelectedReleases = sortedReleases.slice(-maxReleases);
        }

        setSelectedReleases(newSelectedReleases);
        handleFilterChange(newSelectedReleases, selectedType, comparisonMode, selectedStateCategory);
        updateFilter("releases", newSelectedReleases || [])
    };

    const handleTypeChange = (value: string) => {
        setSelectedType(value);
        handleFilterChange(selectedReleases, value, comparisonMode, selectedStateCategory);
        updateFilter("workItemType", value)
    };

    const handleComparisonChange = (value: string) => {
        setComparisonMode(value);
        handleFilterChange(selectedReleases, selectedType, value, selectedStateCategory);
        updateFilter("compareWith", value)
    };

    useEffect(() => {
        const topReleases = releaseDetails
            .flatMap((detail: { releases: any }) => detail.releases)
            .slice(0, comparisonMode === 'previousRelease' ? 4 : 3)
            .map((release: { release: any }) => release.release);

        const storedFilters = JSON.parse(localStorage.getItem(getFilterStorageKey(selectedProduct)) as string);
        if (comparisonMode !== 'previousRelease' && selectedReleases.length > 3) {
            setSelectedReleases(selectedReleases.slice(0, 3));
            handleFilterChange(selectedReleases.slice(0, 3), selectedType, comparisonMode, selectedStateCategory);
        } else if (!storedFilters?.releases || storedFilters?.releases.length === 0) {
            setSelectedReleases(topReleases);
            handleFilterChange(topReleases, selectedType, comparisonMode, selectedStateCategory);
        }
    }, [releaseDetails, comparisonMode]);



    useEffect(() => {
        if (releaseDetails.length === 0) {
            return;
        }

        const queryParams = new URLSearchParams(location.search);
        const insight = queryParams.get("insight");
        const releases = queryParams.get("releases");

        if (insight) {
            setInsightDescription(decodeURIComponent(insight));
        }
        let Releases: string[] = [];

        if (releases) {
            Releases = releases.split(",").filter(r => r !== "null" && r.trim() !== "");
            setSelectedReleases(Releases);
            handleFilterChange(Releases, selectedType, comparisonMode, selectedStateCategory);
            updateFilter("releases", Releases || [])
        }

        if (selectedInsightWorkItems && selectedInsightWorkItems.length > 0) {
            const latestWorkItems = releaseDetails
                .flatMap((releaseDetail: { releases: any[] }) =>
                    releaseDetail.releases
                        .filter((release) => Releases.includes(release.release))
                        .flatMap((release) => release.work_items.map((item: { work_item_ID: any; title: string }) => ({
                            work_item_ID: item.work_item_ID,
                            title: item.title
                        })))
                );

            const filteredWorkItems = latestWorkItems.filter((workItem: { work_item_ID: any; }) =>
                selectedInsightWorkItems.includes(workItem.work_item_ID)
            );

            setInsightWorkItems(filteredWorkItems);
        }
    }, [location.search, releaseDetails]);


    const handleWorkItemClick = (workItemId: any) => {
        navigate(`/work-item?viewMode=WorkItem&workItemId=${workItemId}`);
        setWorkItemClicked(true)
        setIsJourneyView(true)
        setInsightDescription("")
    };


    const handleExecutionTimeChange = (value: string) => {

        if (value === ExecutionTime.FIRST_PASS) {
            setIsFirstPassChecked(true)
            setIsReworkChecked(false)
            updateFilter("firstPass", true)
            updateFilter('rework', false)
            setExecutionTime(ExecutionTime.FIRST_PASS)
            updateFilter('executionTime', ExecutionTime.FIRST_PASS)
        } else if (value === ExecutionTime.REWORK) {
            setIsReworkChecked(true)
            setIsFirstPassChecked(false)
            updateFilter("firstPass", false)
            updateFilter('rework', true)
            setExecutionTime(ExecutionTime.REWORK)
            updateFilter('executionTime', ExecutionTime.REWORK)
        } else {
            setIsFirstPassChecked(true)
            setIsReworkChecked(true)
            updateFilter("firstPass", true)
            updateFilter("rework", true)
            setExecutionTime(ExecutionTime.TOTAL)
            updateFilter('executionTime', ExecutionTime.TOTAL)
        }
    };

    const renderDropdown = (menu: ReactElement) => {
        return releaseDetails?.length ? (
            <div className="multi-select-dropdown">
                {releaseDetails
                    .flatMap((releaseDetail: any) => releaseDetail.releases)
                    .map((release: any) => release.release)
                    .sort(compareVersions)
                    .map((release: string) => {
                        const isChecked = selectedReleases.includes(release);
                        const isDisabled = !isChecked && selectedReleases.length >= (comparisonMode === 'previousRelease' ? 4 : 3);

                        return (
                            <div key={release} className="dropdown-item">
                                <Checkbox
                                    onChange={() => handleCheckboxChange(release)}
                                    checked={isChecked}
                                    disabled={isDisabled}
                                >
                                    {release}
                                </Checkbox>
                            </div>
                        );
                    })}
            </div>
        ) : (
            <div>No releases available</div>
        );
    };

    const flattenData = (data: any) => {
        const stateCategories = data[0].state_categories[0]?.datasets;

        return data[0].releases.flatMap((release: any) =>
            release.work_items.map((workItem: any) => {
                const executionTimesFP: any = {};
                const executionTimesRework: any = {};
                let totalReworkDuration = 0;

                workItem.execution_time.forEach((et: any) => {
                    executionTimesFP[et.category] = parseDurations(et.first_pass).toFixed(1) || 0;
                    executionTimesRework[et.category] = parseDurations(et.rework).toFixed(1) || 0;
                    totalReworkDuration += parseDurations(et.rework);
                });

                return {
                    'Release Name': release.release,
                    'Release Date': release.date,
                    'Work Item ID': workItem.work_item_ID,
                    'Work Item Type': workItem.type,
                    'Lead Time': calculateLeadTime(workItem.execution_time),
                    ...stateCategories.reduce((acc: any, stateCategory: any) => {
                        acc['Units'] = stateCategory.units;
                        acc[`${stateCategory.category} First Pass`] = executionTimesFP[stateCategory.category] || 0;
                        acc[`${stateCategory.category} Rework`] = executionTimesRework[stateCategory.category] || 0;
                        return acc;
                    }, {}),
                    'Created At': workItem.created_date,
                    'Project': workItem.project,
                    'Rework': totalReworkDuration > 0,
                    'Title': workItem.title.replace(/,/g, "")
                };
            })
        );
    };

    const calculateLeadTime = (executionTime: any) => {
        return executionTime.reduce((total: any, et: any) => {
            return total + parseDurations(et.first_pass) + parseDurations(et.rework);
        }, 0).toFixed(2);
    };

    const parseDurations = (duration: any) => {
        if (!duration) return 0;

        const daysMatch = duration.match(/(\d+) days?/);
        const timeMatch = duration.match(/(\d+):(\d+):(\d+)/);

        const days = daysMatch ? parseInt(daysMatch[1]) : 0;
        const hours = timeMatch ? parseInt(timeMatch[1]) : 0;
        const minutes = timeMatch ? parseInt(timeMatch[2]) : 0;
        const seconds = timeMatch ? parseInt(timeMatch[3]) : 0;

        return days + (hours / 24) + (minutes / 1440) + (seconds / 86400);
    };

    const convertToCSV = (data: any) => {
        if (!data || !data.length) return "";

        const headers = Object.keys(data[0]).join(",") + "\n";
        const rows = data
            .map((row: any) => Object.values(row).join(","))
            .join("\n");

        return headers + rows;
    };

    const downloadCSV = async () => {

        const data = flattenData(filteredData);
        const csv = convertToCSV(data);

        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const fileName = `${selectedProduct}_WorkItemLeadTimes_${dayjs().format('YYYY_MM_DD')}`
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleStateCategoriesChange = (value: string) => {
        setSelectedStateCategory(value);
        handleFilterChange(selectedReleases, selectedType, comparisonMode, value);
    };

    if (loading) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
                <Spin size="large" />
                <p className="mt-4 text-lg font-medium text-gray-600">Loading...</p>
            </div>
        );
    }
    
    if (releaseDetails.length === 0) {
        return (
          <div className="fixed inset-0 flex items-center justify-center bg-white">
            <p className="text-center text-gray-500 text-lg">No data found</p>
          </div>
        );
      }

    return (
        <div className='p-4 overflow-hidden'>
            {insightDescription && (
                <h2 className="text-[24px] font-semibold text-center mb-2">{insightDescription}</h2>
            )}
            <Button shape="circle" className="action-circle-btn" style={{ position: 'absolute', right: '16px', fontSize: "16px" }}>
                <DownloadOutlined onClick={() => downloadCSV()} />
            </Button>
            <Row>
                <Row gutter={[16, 0]} style={{ marginRight: '8px', display: 'flex', flexDirection: 'column', marginTop: '-6px' }}>
                    <h1 className="card-title">Release Comparison</h1>
                    <h2 className="sub-heading">Lead Time ({leadTimeUnits})</h2>
                </Row>
                {productList?.length > 1 &&
                    <Row gutter={[0, 16]} className='w-full max-w-36 ml-5' >
                        <ProductDropdown />
                    </Row>}
                <Row gutter={[0, 16]} className="release-dropdown mb-2 ml-2" style={{ width: '100%', display: 'flex', flexDirection: 'column', maxWidth: calculateDropdownWidth(selectedReleases), }}>
                    <Col span={24}>
                        <Col span={24}>
                            <Text>Releases</Text>
                        </Col>
                        <div className="custom-dropdown-wrapper">
                            <Select
                                mode="multiple"
                                value={selectedReleases}
                                onChange={handleReleaseChange}
                                className="custom-dropdown"
                                dropdownRender={renderDropdown}
                                maxTagCount={comparisonMode === 'previousRelease' ? 4 : 3}
                            >
                                {releaseDetails.map((releaseDetail: any) =>
                                    releaseDetail.releases.map((release: any) => (
                                        <Option key={release.release} value={release.release}>
                                            {release.release}
                                        </Option>
                                    ))
                                )}
                            </Select>
                        </div>
                    </Col>
                </Row>
                <Row gutter={[0, 16]} className="release-dropdown mb-4" style={{ width: '100%', maxWidth: "150px" }}>
                    <Col span={24}>
                        <Col span={24}>
                            <Text>Work Item Type</Text>
                        </Col>
                        <div className="custom-dropdown-wrapper">
                            <Select
                                value={selectedType}
                                onChange={handleTypeChange}
                                className="custom-dropdown"
                                placeholder="Select type"
                            >
                                <Option value={ALL}>All</Option>
                                {workItemTypes.map((type) => (
                                    <Option key={type} value={type}>
                                        {type}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                </Row>
                <Row gutter={[0, 16]} className="release-dropdown mb-4" style={{ width: '100%', maxWidth: "150px" }}>
                    <Col span={24}>
                        <Col span={24}>
                            <Text>Start from</Text>
                        </Col>
                        <div className="custom-dropdown-wrapper">
                            <Select
                                value={selectedStateCategory && selectedStateCategory.length > 0 ? selectedStateCategory : stateCategories[0]}
                                onChange={handleStateCategoriesChange}
                                placeholder="Select category"
                                className="custom-dropdown"
                            >
                                {stateCategories.map((type: any) => (
                                    <Option key={type} value={type}>
                                        {type}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                </Row>

                <Row gutter={[0, 16]} className="release-dropdown mb-4" style={{ width: '100%', maxWidth: "150px" }}>
                    <Col span={24}>
                        <Col span={24}>
                            <Text>Compare with</Text>
                        </Col>
                        <div className="custom-dropdown-wrapper">
                            <Select
                                value={
                                    filteredData?.[0]?.state_categories?.[0]?.datasets?.some(
                                    (item: { target: number | null | undefined }) => item?.target !== null && item?.target !== undefined
                                    )
                                    ? comparisonMode
                                    : 'previousRelease'
                                }                                
                                onChange={handleComparisonChange}
                                className="custom-dropdown"
                                placeholder="Select comparison"
                                >
                                {filteredData?.[0]?.state_categories?.[0]?.datasets?.some(
                                            (item: { target: number | null | undefined }) => item?.target !== null && item?.target !== undefined
                                            ) 
                                            ? <>
                                            <Option value="target">Target</Option>
                                            <Option value="previousRelease">Previous Release</Option>
                                        </>
                                        : <Option value="previousRelease">Previous Release</Option>
                                    }
                                </Select>
                            </div>
                    </Col>
                </Row>

                <Row gutter={[0, 16]} className="release-dropdown mb-6" style={{ width: '100%', maxWidth: "100px" }}>
                    <Col span={24}>
                        <Col span={24}>
                            <Text className='size-2'>Execution Time</Text>
                        </Col>
                        <div className="custom-dropdown-wrapper">
                            <Select
                                defaultValue="total"
                                value={executionTime}
                                options={executionTimeOptions}
                                onChange={handleExecutionTimeChange}
                                className="custom-dropdown"
                            />
                        </div>
                    </Col>
                </Row>


            </Row>
            <Row gutter={[16, 16]} >
                {comparisonMode !== 'previousRelease' ?
                    <Col span={5} style={{ top: 140 }}>
                        {filteredData.length > 0 && filteredData[0].state_categories ? (
                            <>
                                <Tooltip title={`Target: ${filteredData[0].state_categories[0]?.datasets.reduce((sum: number, category: { target: any; }) => sum + parseFloat(category.target || 0), 0)} ${filteredData[0].state_categories[0]?.units || ''}`}>
                                    <Row style={{ marginBottom: '12px' }}>
                                        <Collapse
                                            bordered={false}
                                            expandIcon={() => null}
                                            activeKey={[]}
                                            style={{
                                                backgroundColor: 'white',
                                                marginBottom: '-3px',
                                                border: '1px solid #f0f0f0',
                                                borderRadius: '2px',
                                                width: '100%',
                                            }}
                                        >
                                            <Panel
                                                header={
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            height: '24px',
                                                        }}
                                                    >
                                                        <h3 style={{ margin: 0, fontSize: '16px' }}>Overall</h3>
                                                        <ValueDisplay value={totalAverageDifference} />
                                                    </div>
                                                }
                                                style={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: '4px',
                                                }}
                                                key={''}
                                            ></Panel>
                                        </Collapse>
                                    </Row>
                                </Tooltip>

                                {filteredData[0].state_categories[0]?.datasets.map(
                                    (category: { category: string; target: string; units: string }, index: number) => {

                                        const averageCategoryDifference = releaseDifferences.averages.find(
                                            (avg) => avg.category === category.category
                                        )?.difference || 0;

                                        return (
                                            <Tooltip title={`Target: ${category.target} ${category.units}`}>
                                                <Row key={index} style={{ marginBottom: '12px' }}>
                                                    <Collapse
                                                        key={category.category}
                                                        bordered={false}
                                                        expandIcon={() => null}
                                                        activeKey={[]}
                                                        style={{
                                                            backgroundColor: 'white',
                                                            marginBottom: '-2px',
                                                            border: '1px solid #f0f0f0',
                                                            borderRadius: '2px',
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <Panel
                                                            key={`panel-${index}`}
                                                            header={
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        height: '24px',
                                                                    }}
                                                                >
                                                                    <h3 style={{ margin: 0, fontSize: '16px' }}>{category.category}</h3>
                                                                    <div style={{ display: 'flex', gap: '16px' }}>
                                                                        <ValueDisplay value={averageCategoryDifference} />
                                                                    </div>
                                                                </div>
                                                            }
                                                            style={{
                                                                backgroundColor: 'white',
                                                                border: '1px solid #f0f0f0',
                                                                borderRadius: '4px',
                                                            }}
                                                        ></Panel>
                                                    </Collapse>
                                                </Row>
                                            </Tooltip>
                                        );
                                    }
                                )}

                            </>
                        ) : (
                            <Row>
                                <Card
                                    style={{
                                        width: '100%',
                                        textAlign: 'center',
                                        padding: '24px',
                                        border: '1px solid #f0f0f0',
                                        borderRadius: '8px',
                                    }}
                                >
                                    No categories available
                                </Card>
                            </Row>
                        )}
                    </Col>
                    : null
                }


                {filteredData.map((item: IReleaseItems
                ) => {
                    return item.releases.map((releaseItem) => {

                        const stateCategories = filteredData.length > 0 ? filteredData[0]?.state_categories ?? [] : [];

                        const overallDifference = releaseDifferences[releaseItem.release]?.find(
                            (item) => item.category === 'Overall'
                        )?.difference;
                        const releases = Object.keys(releaseDifferences).filter(
                            (key) => key !== "averages"
                        );
                        const isFirstRelease = releases.indexOf(releaseItem.release) === 0;

                        return (
                            <Col span={5} key={releaseItem.release}>
                                <Card
                                        title={
                                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                              <Button onClick={() => showModal(releaseItem)}>
                                                {`Release: ${releaseItem.release}`}
                                              </Button>
                                            </div>
                                          }
                                    style={{ backgroundColor: 'white' }}
                                >

                                    <div className="w-full h-auto m-0 p-0 relative" >
                                        <ReworkBarChart reworkItems={releaseItem?.rework} selectedType={selectedType} />
                                    </div>

                                    <Collapse
                                        key="overall-rating"
                                        style={{
                                            backgroundColor: 'white',
                                            marginBottom: '12px',
                                            border: '1px solid #f0f0f0',
                                            borderRadius: '2px',
                                        }}
                                    >
                                        <Panel
                                            header={
                                                <Tooltip title={`Target: ${stateCategories[0]?.datasets.reduce((sum: number, category: { target: any; }) => sum + parseFloat(category.target || 0), 0)} ${filteredData[0].state_categories[0]?.units || ''}`}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        height: '18px'
                                                    }}>
                                                        <h3 style={{ margin: 0, fontSize: '16px', cursor: 'pointer' }}>Overall</h3>
                                                        <ValueDisplay value={overallDifference ?? 0} showSign={!(isFirstRelease && comparisonMode === "previousRelease")} />
                                                    </div>
                                                </Tooltip>
                                            }
                                            style={{
                                                backgroundColor: 'white',
                                                border: '1px solid #f0f0f0',
                                                borderRadius: '4px',
                                            }} key={''}                    >
                                            {releaseItem.work_items
                                                .sort((a, b) => {
                                                    const aFirstPass = a.execution_time[0]?.first_pass || '0m';
                                                    const bFirstPass = b.execution_time[0]?.first_pass || '0m';

                                                    const aRework = a.execution_time[0]?.rework || '0m';
                                                    const bRework = b.execution_time[0]?.rework || '0m';

                                                    const aFirstPassDuration = parseDuration(aFirstPass).totalDays;
                                                    const bFirstPassDuration = parseDuration(bFirstPass).totalDays;

                                                    const aReworkDuration = parseDuration(aRework).totalDays;
                                                    const bReworkDuration = parseDuration(bRework).totalDays;

                                                    if (selectedOptions.firstPass && selectedOptions.rework) {
                                                        return (bFirstPassDuration + bReworkDuration) - (aFirstPassDuration + aReworkDuration);
                                                    } else if (selectedOptions.firstPass) {
                                                        return bFirstPassDuration - aFirstPassDuration;
                                                    } else if (selectedOptions.rework) {
                                                        return bReworkDuration - aReworkDuration;
                                                    }

                                                    return bFirstPassDuration - aFirstPassDuration;
                                                })
                                                .slice(0, 3)
                                                .map((workItem) => {
                                                    const totalDuration = workItem.execution_time.reduce((total, time) => {
                                                        let duration = 0;

                                                        if (selectedOptions.firstPass && time.first_pass) {
                                                            duration += parseDuration(time.first_pass).totalDays;
                                                        }

                                                        if (selectedOptions.rework && time.rework) {
                                                            duration += parseDuration(time.rework).totalDays;
                                                        }

                                                        return total + duration;
                                                    }, 0);



                                                    return (
                                                        <p
                                                            key={workItem.work_item_ID}
                                                            style={{
                                                                margin: '4px 0',
                                                                color: '#595959',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                borderBottom: '1px solid #f0f0f0',
                                                                paddingBottom: '8px',
                                                            }}
                                                        >
                                                            <span
                                                             onClick={() => workItem.work_item_ID && handleWorkItemClick(workItem.work_item_ID)}
                                                                style={{
                                                                    display: 'inline-block',
                                                                    maxWidth: '120px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    cursor: 'pointer',
                                                                    textDecoration: 'underline',
                                                                }}
                                                            >
                                                                {workItem.work_item_ID}: {workItem.title}
                                                            </span>
                                                            <strong style={{ marginLeft: '8px' }}>
                                                                {totalDuration.toFixed(1)}d
                                                            </strong>
                                                        </p>
                                                    );
                                                })}

                                            {releaseItem.work_items.length > 3 && (
                                                <div>
                                                    {(() => {
                                                        const remainingItems = releaseItem.work_items.slice(3);

                                                        const totalRemainingTime = remainingItems.reduce((total, workItem) => {
                                                            const totalWorkItemDuration = workItem.execution_time.reduce((itemTotal, time) => {
                                                                const parsedDuration = parseDuration(time.first_pass);
                                                                return itemTotal + parsedDuration.totalDays;
                                                            }, 0);

                                                            return total + totalWorkItemDuration;
                                                        }, 0);

                                                        const averageRemainingTime = remainingItems.length > 0 ? totalRemainingTime / remainingItems.length : 0;

                                                        return (
                                                            <p
                                                                style={{
                                                                    margin: '4px 0',
                                                                    color: '#595959',
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    borderBottom: '1px solid #f0f0f0',
                                                                    paddingBottom: '8px',
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        display: 'inline-block',
                                                                        maxWidth: '120px',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    Avg. {remainingItems.length} Other items
                                                                </span>
                                                                <strong style={{ marginLeft: '8px' }}>
                                                                    {averageRemainingTime.toFixed(1)}d
                                                                </strong>
                                                            </p>
                                                        );
                                                    })()}
                                                </div>
                                            )}

                                        </Panel>
                                    </Collapse>

                                    {stateCategories[0]?.datasets
                                        .filter((item: { category: string; }) => typeof item.category === "string" && item.category.trim() !== "" && item.category !== "null")
                                        .map((item: { category: string; target: number; units: string }) => {
                                            const { category, target, units } = item;

                                            const difference = releaseDifferences[releaseItem.release]?.find(
                                                (diff) => diff.category === category
                                            )?.difference;

                                            const releases = Object.keys(releaseDifferences).filter(
                                                (key) => key !== "averages"
                                            );
                                            const isFirstRelease = releases.indexOf(releaseItem.release) === 0;

                                            return (
                                                <Collapse
                                                    key={category}
                                                    style={{
                                                        backgroundColor: 'white',
                                                        marginBottom: '12px',
                                                        border: '1px solid #f0f0f0',
                                                        borderRadius: '2px',
                                                    }}
                                                >
                                                    <Panel
                                                        header={
                                                            <Tooltip title={`Target: ${target} ${units}`}>
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        height: '18px',
                                                                    }}
                                                                >
                                                                    <h3 style={{ margin: 0, fontSize: '16px' }}>{category}</h3>
                                                                    <ValueDisplay value={difference ?? 0} showSign={!(isFirstRelease && comparisonMode === "previousRelease")} />
                                                                </div>
                                                            </Tooltip>
                                                        }
                                                        key={category}
                                                        style={{
                                                            backgroundColor: 'white',
                                                            border: '1px solid #f0f0f0',
                                                            borderRadius: '4px',
                                                        }}
                                                    >
                                                        <div style={{ padding: '12px 0' }}>
                                                            {releaseItem.work_items
                                                                .sort((a, b) => {
                                                                    const aFirstPass = a.execution_time.find(time => time.category === category)?.first_pass || '0m';
                                                                    const bFirstPass = b.execution_time.find(time => time.category === category)?.first_pass || '0m';
                                                                    const aRework = a.execution_time.find(time => time.category === category)?.rework || '0m';
                                                                    const bRework = b.execution_time.find(time => time.category === category)?.rework || '0m';

                                                                    const aFirstPassDuration = parseDuration(aFirstPass).totalDays;
                                                                    const bFirstPassDuration = parseDuration(bFirstPass).totalDays;
                                                                    const aReworkDuration = parseDuration(aRework).totalDays;
                                                                    const bReworkDuration = parseDuration(bRework).totalDays;

                                                                    if (selectedOptions.firstPass && selectedOptions.rework) {
                                                                        return (bFirstPassDuration + bReworkDuration) - (aFirstPassDuration + aReworkDuration);
                                                                    } else if (selectedOptions.firstPass) {
                                                                        return bFirstPassDuration - aFirstPassDuration;
                                                                    } else if (selectedOptions.rework) {
                                                                        return bReworkDuration - aReworkDuration;
                                                                    }

                                                                    return bFirstPassDuration - aFirstPassDuration;
                                                                })
                                                                .slice(0, 3)
                                                                .map((workItem) => {
                                                                    const categoryExecutionTime = workItem.execution_time.filter(time => time.category === category);

                                                                    const totalDuration = categoryExecutionTime.reduce((total, time) => {
                                                                        let duration = 0;

                                                                        if (selectedOptions.firstPass && time.first_pass) {
                                                                            duration += parseDuration(time.first_pass).totalDays;
                                                                        }

                                                                        if (selectedOptions.rework && time.rework) {
                                                                            duration += parseDuration(time.rework).totalDays;
                                                                        }

                                                                        return total + duration;
                                                                    }, 0);
                                                                    return (
                                                                        <p
                                                                            key={workItem.work_item_ID}
                                                                            style={{
                                                                                margin: '4px 0',
                                                                                color: '#595959',
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'center',
                                                                                borderBottom: '1px solid #f0f0f0',
                                                                                paddingBottom: '8px',
                                                                            }}
                                                                        >
                                                                            <span
                                                                               onClick={() => workItem.work_item_ID && handleWorkItemClick(workItem.work_item_ID)}
                                                                                style={{
                                                                                    display: 'inline-block',
                                                                                    maxWidth: '120px',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                    cursor: 'pointer',
                                                                                    textDecoration: 'underline',
                                                                                }}
                                                                            >
                                                                                {workItem.work_item_ID}: {workItem.title}
                                                                            </span>
                                                                            <strong style={{ marginLeft: '8px' }}>
                                                                                {totalDuration.toFixed(1)}d
                                                                            </strong>
                                                                        </p>
                                                                    );
                                                                })}
                                                            {releaseItem.work_items.length > 3 && (
                                                                <div>
                                                                    {(() => {
                                                                        const remainingItems = releaseItem.work_items.slice(3);

                                                                        const totalRemainingTime = remainingItems.reduce((total, workItem) => {
                                                                            const categoryExecutionTime = workItem.execution_time.filter(time => time.category === category);

                                                                            let totalDurationForItem = 0;

                                                                            categoryExecutionTime.forEach(time => {
                                                                                if (selectedOptions.firstPass && time.first_pass) {
                                                                                    totalDurationForItem += parseDuration(time.first_pass).totalDays;
                                                                                }
                                                                                if (selectedOptions.rework && time.rework) {
                                                                                    totalDurationForItem += parseDuration(time.rework).totalDays;
                                                                                }
                                                                            });

                                                                            return total + totalDurationForItem;
                                                                        }, 0);

                                                                        const averageRemainingTime = remainingItems.length > 0 ? totalRemainingTime / remainingItems.length : 0;

                                                                        return (
                                                                            <p
                                                                                // key={workItem.work_item_ID}
                                                                                style={{
                                                                                    margin: '4px 0',
                                                                                    color: '#595959',
                                                                                    display: 'flex',
                                                                                    justifyContent: 'space-between',
                                                                                    alignItems: 'center',
                                                                                    borderBottom: '1px solid #f0f0f0',
                                                                                    paddingBottom: '8px',
                                                                                }}
                                                                            >
                                                                                <span
                                                                                    style={{
                                                                                        display: 'inline-block',
                                                                                        maxWidth: '120px',
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap',
                                                                                    }}
                                                                                >
                                                                                    Avg. {remainingItems.length} Other items
                                                                                </span>
                                                                                <strong style={{ marginLeft: '8px' }}>{averageRemainingTime.toFixed(1)}d</strong>
                                                                            </p>

                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Panel>

                                                </Collapse>
                                            );
                                        })}
                                </Card>
                            </Col>

                        );
                    });
                })}
                {insightDescription.length > 0 && (
                    <Col span={4} className="p-4 bg-white rounded-lg shadow-md">
                        <h1 className="card-title mb-3">Most Impacked Work Items</h1>
                        {insightWorkItems && insightWorkItems.length > 0 ? (
                            <ul className="space-y-2">
                                {insightWorkItems.map((workItem: { work_item_ID: number; title: string }) => (
                                    <li
                                        key={workItem.work_item_ID}
                                        className="flex items-center space-x-2 bg-[#f0f8ff] px-3 py-2 rounded-md border border-[#e0f7fa]-300 shadow-sm hover:shadow-md transition duration-200 cursor-pointer"
                                        onClick={() => handleWorkItemClick(workItem.work_item_ID)}
                                    >
                                        <span className="text-black-600">•</span>
                                        <span className="text-black-600 font-medium hover:text-blue-800">
                                            {workItem.work_item_ID} - {workItem.title}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-500">No work items available</p>
                        )}
                    </Col>
                )}

            </Row>

            {isModalVisible && (
                <FullDetailsModal
                    onClose={handleCancel}
                    releaseItem={selectedReleaseItem}
                    selectedOptions={selectedOptions}
                />
            )}

        </div>
    )
}

export default ReleaseComparison;
